import { PropTypes } from 'typed/imports';
import { PureComponent, ReactEventHandler, ComponentEventHandler1 } from 'typed/components/utils';
import { ReactNode } from 'react';
import { default as FormattedNumber } from '../FormattedNumber/FormattedNumber';
import { default as FormattedResource, Resource, resourcePropType, isResource } from '../FormattedResource/FormattedResource';
import './SliderInput.less';

interface Props {
    value?: number;
    min?: number;
    max?: number;
    step?: number;
    minLabel?: Resource | ReactNode | null;
    maxLabel?: Resource | ReactNode | null;
    majorTickStep?: number | null;
    majorTickFormat?: ((v: number) => ReactNode) | string | null;
    minorTickStep?: number | null;
    minorTickFormat?: ((v: number) => ReactNode) | string | null;
    debounce?: number;
    disabled?: boolean;
    className?: string;
    onChange?: ComponentEventHandler1<number>;
    children?: never;
}

interface State {
    value: number;
}

export default class SliderInput extends PureComponent<Props> {
    static readonly propTypes = {
        value: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
        step: PropTypes.number,
        minLabel: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        maxLabel: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        majorTickStep: PropTypes.number,
        majorTickFormat: PropTypes.string,
        minorTickStep: PropTypes.number,
        minorTickFormat: PropTypes.string,
        debounce: PropTypes.number,
        disabled: PropTypes.bool,
        className: PropTypes.string,
        onChange: PropTypes.func
    }

    static readonly defaultProps = {
        value: 0,
        min: 0,
        max: 1,
        step: 0.01,
        majorTickStep: 0.5,
        debounce: 100,
        className: ''
    }

    private _debounceTimeoutID: ReturnType<typeof setTimeout> | null = null;

    state: State = { value: 0 };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props === nextProps || this.props.value !== nextProps.value) {
            this._cancelDebounce();

            this.setState({ value: nextProps.value });
        }
    }

    componentWillUnmount() {
        if (this._debounceTimeoutID) {
            this._onChangeDebounced();
        }
    }

    private _cancelDebounce() {
        if (this._debounceTimeoutID) {
            clearTimeout(this._debounceTimeoutID);
            this._debounceTimeoutID = null;
        }
    }

    private _onChangeDebounced = () => {
        this._cancelDebounce();

        if (this.props.disabled) {
            return;
        }

        if (this.props.onChange && this.props.value !== this.state.value) {
            this.props.onChange(this, this.state.value);
        }
    }

    private _onChange: ReactEventHandler = ({ target }) => {
        this._cancelDebounce();

        if (this.props.disabled) {
            return;
        }

        this.setState({ value: +(target as any).value });

        this._debounceTimeoutID = setTimeout(this._onChangeDebounced, this.props.debounce || 0);
    }

    private _onBlur: ReactEventHandler = () => {
        if (this.props.disabled) {
            return;
        }

        this._onChangeDebounced();
    }

    render() {
        const
            { minLabel, maxLabel, majorTickStep, majorTickFormat, minorTickStep, minorTickFormat, disabled, className } = this.props,
            step = Math.abs(this.props.step!) || 0.01,
            min = this.props.min!,
            max = Math.max(min + step, this.props.max!),
            range = max - min,
            majorTicks: number[] = [],
            minorTicks: number[] = [],
            origin = min * max >= 0 ? min : 0;

        if (majorTickStep) {
            for (let n = origin - majorTickStep; n >= min; n -= majorTickStep) {
                majorTicks.unshift(n);
            }
            for (let p = origin; p <= max; p += majorTickStep) {
                majorTicks.push(p);
            }
        }

        if (minorTickStep) {
            for (let n = origin - minorTickStep; n >= min; n -= minorTickStep) {
                minorTicks.unshift(n);
            }
            for (let p = origin; p <= max; p += minorTickStep) {
                minorTicks.push(p);
            }
        }

        return (
            <div className={`slider-input flex-row ${className}`}>
                {
                    minLabel ?
                        <div className="label" >{isResource(minLabel) ? <FormattedResource resource={minLabel} /> : minLabel}</div> :
                        null
                }
                <div className="main-col" >
                    <input ref="input" type="range" min={min} max={max} step={step} value={this.state.value} disabled={disabled} onChange={this._onChange} onBlur={this._onBlur} />
                    {
                        minorTicks.length || majorTicks.length ?
                            <div className="ticks">
                                {minorTicks.map((value) =>
                                    <div key={value} className="tick minor" style={{ left: `${100 * (value - min) / range}%` }}>
                                        {
                                            typeof minorTickFormat === 'function' ? <div className="text">{minorTickFormat(value)}</div> :
                                                minorTickFormat ? <div className="text"><FormattedNumber format={minorTickFormat} value={value} /></div> :
                                                    null
                                        }
                                    </div>
                                )}
                                {majorTicks.map((value) =>
                                    <div key={value} className="tick major" style={{ left: `${100 * (value - min) / range}%` }}>
                                        {
                                            typeof majorTickFormat === 'function' ? <div className="text">{majorTickFormat(value)}</div> :
                                                majorTickFormat ? <div className="text"><FormattedNumber format={majorTickFormat} value={value} /></div> :
                                                    null
                                        }
                                    </div>
                                )}
                            </div> :
                            null
                    }
                </div>
                {
                    maxLabel ?
                        <div className="label" >{isResource(maxLabel) ? <FormattedResource resource={maxLabel} /> : maxLabel}</div> :
                        null
                }
            </div>
        );
    }
}